import type { SharingProfileStateType } from '@/features/sharing-profile/types/sharing-profile.interface';

/**
 * Draft shape for the create/edit form's identity/description concerns only.
 *
 * Deliberately excludes fields already owned as top-level state by other slices:
 * `time_rules`, `ip_rules`, `timezone`, `ipRestrictions` (policies slice) and
 * `users`, `groups` (user-management slice). `resources`/`prevResources`/`resourcesValid`
 * remain here because no slice owns them yet.
 */
export type ProfileDraft = Pick<
  SharingProfileStateType,
  'id' | 'space_id' | 'name' | 'description' | 'resources' | 'prevResources' | 'resourcesValid'
>;

export interface SharingProfilesState {
  /** Sharing profiles loaded for the current space. */
  profiles: SharingProfileStateType[];
  /** ID of the profile currently selected (e.g. for viewing/editing). Undefined when none is selected. */
  selectedProfileId: string | undefined;
  /** Draft profile backing the create/edit form/stepper. Undefined when no draft is active. */
  profileDraft: ProfileDraft | undefined;
  /** True while profiles are being fetched or mutated. */
  isLoading: boolean;
  /** Last error message from a profiles operation. Undefined when there is no error. */
  error: string | undefined;
}

export const initialProfilesState: SharingProfilesState = {
  profiles: [],
  selectedProfileId: undefined,
  profileDraft: undefined,
  isLoading: false,
  error: undefined,
};
